import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useEffect, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import type { CompanyImageSearchItemType } from 'types/api/companyImageSearchItemType';
import type { MasterRefResponseType } from 'types/api/masterRefResponseType';
import {
  buildFailedErrorMessage,
  imageTypes,
  MAX_FILE_SIZE,
  MAX_UPLOAD_FILES,
  processFilesUpload,
  sortImages,
  validateFiles,
} from './companyImageGallery.utils';

export type UseCompanyImageGalleryProps = {
  setOpen: (open: boolean) => void;
  open: boolean;
  onImageSelected: (imageKey: string) => void;
};

export type UseCompanyImageGalleryReturn = {
  /* state */
  initialized: boolean;
  processing: boolean;
  keywordState: string;
  sortState: 'newest' | 'file' | 'oldest';
  selectedImageState: CompanyImageSearchItemType | null;
  companyImagesState: CompanyImageSearchItemType[];
  dropdownOpen: boolean;
  uploadErrorState: string | null;
  isDragging: boolean;
  tab: 'ai' | 'upload';
  aiPrompt: string;
  aiLoading: boolean;
  /* refs */
  inputFileRef: React.RefObject<HTMLInputElement | null>;
  dropdownRef: React.RefObject<HTMLDivElement | null>;
  /* setters（JSX からの直接更新用） */
  setTab: (tab: 'ai' | 'upload') => void;
  setAiPrompt: (prompt: string) => void;
  setKeywordState: (keyword: string) => void;
  /* event handlers */
  onAfterOpen: () => Promise<void>;
  handleClose: () => void;
  handleOpenFileUploader: () => void;
  handleSelectImage: (imageId: number) => void;
  handleImgFileInput: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDelete: () => Promise<void>;
  handleSubmit: () => void;
  handleToggleDropdown: () => void;
  handleSort: (sortType: 'newest' | 'file' | 'oldest') => void;
  handleAiGenerate: () => Promise<void>;
  /* computed values */
  activePlan: MasterRefResponseType['mPlans'][0] | undefined;
  isAiImageCountLimit: boolean;
};

/**
 * カンパニー画像ギャラリーモーダルの state 管理・イベントハンドラをまとめたカスタムフック。
 */
export function useCompanyImageGallery(
  props: UseCompanyImageGalleryProps
): UseCompanyImageGalleryReturn {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const [acctState, setAcctState] = useAtom(AcctState);

  const { noticeMessageFailure, getActivePlan } = useAppState();
  const {
    postCompanyImageRegister,
    postCompanyImageSearch,
    postCompanyImageDelete,
    postCompanyImageRegisterAi,
  } = useApiState();

  const [initialized, setInitialized] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [keywordState, setKeywordState] = useState('');
  const [sortState, setSortState] = useState<'newest' | 'file' | 'oldest'>('newest');
  const [selectedImageState, setSelectedImageState] = useState<CompanyImageSearchItemType | null>(
    null
  );
  const [companyImagesState, setCompanyImagesState] = useState<CompanyImageSearchItemType[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [uploadErrorState, setUploadErrorState] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const inputFileRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [tab, setTab] = useState<'ai' | 'upload'>('ai');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const activePlan = getActivePlan();

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * ドロップダウン外クリック時の処理
   * 背景スクロール制御は ReactModal の bodyOpenClassName="ReactModal__body--open" に委任済み。
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 画面の状態を初期化します。
   */
  const _initState = () => {
    setKeywordState('');
    setSortState('newest');
    setSelectedImageState(null);
    setCompanyImagesState([]);
    setDropdownOpen(false);
    setUploadErrorState(null);
    setIsDragging(false);
    setTab('ai');
    setAiPrompt('');
    setAiLoading(false);
  };

  /**
   * AI画像生成数が上限に達しているかどうかを判定します。
   */
  const isAiImageCountLimit =
    acctState.company.aiImageUsageCount >= (activePlan?.aiImageCountLimit ?? 0);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * モーダルを開いた後の処理
   */
  const onAfterOpen = async () => {
    try {
      setInitialized(true);
      // カンパニー画像検索APIの呼び出し
      const response = await postCompanyImageSearch({
        companyId: acctState.company.id,
      });
      setCompanyImagesState(response.images);
    } catch {
      noticeMessageFailure('画像一覧の取得に失敗しました。');
    } finally {
      setInitialized(false);
    }
  };

  /**
   * モーダルを閉じた後の処理
   */
  const handleClose = () => {
    props.setOpen(false);
    _initState();
  };

  /**
   * ファイルアップロードボタン押下時の処理
   */
  const handleOpenFileUploader = () => {
    inputFileRef.current?.click();
  };

  /**
   * 画像選択時の処理
   */
  const handleSelectImage = (imageId: number) => {
    setSelectedImageState(companyImagesState.find(image => image.id === imageId) ?? null);
  };

  /**
   * ファイル選択ダイアログで選択された画像を読み取ります。
   */
  const handleImgFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      await _processFiles(Array.from(e.target.files));
    }
    // 同じファイルを何度も指定できるように値を初期化
    e.target.value = '';
  };

  /**
   * ドラッグオーバー時の処理
   */
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  /**
   * ドラッグリーブ時の処理
   * relatedTarget が button-upload 内の子要素の場合（子要素へのホバー移動）は
   * setIsDragging(false) を呼ばない（ハイライトのちらつき防止）
   */
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    const uploadArea = document.getElementById('button-upload');
    if (uploadArea && !uploadArea.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  /**
   * ドロップ時の処理
   */
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      void _processFiles(Array.from(e.dataTransfer.files));
    }
  };

  /**
   * 削除ボタン押下時の処理
   */
  const handleDelete = async () => {
    if (!selectedImageState) {
      return;
    }
    try {
      // 画像の削除
      await postCompanyImageDelete({
        companyImageId: selectedImageState.id,
      });
    } catch {
      noticeMessageFailure('画像の削除に失敗しました。');
      return;
    }
    // ステートの更新
    setCompanyImagesState(prevState =>
      prevState.filter(image => image.id !== selectedImageState.id)
    );
    setSelectedImageState(null);
  };

  /**
   * 決定ボタン押下時の処理
   */
  const handleSubmit = () => {
    props.onImageSelected(selectedImageState?.imageKey ?? '');
    handleClose();
  };

  /**
   * ドロップダウンの表示/非表示を切り替える
   */
  const handleToggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  /**
   * ソート順を変更する
   */
  const handleSort = (sortType: 'newest' | 'file' | 'oldest') => {
    setCompanyImagesState(sortImages(companyImagesState, sortType));
    setSortState(sortType);
    setDropdownOpen(false);
  };

  /**
   * AI画像生成ボタン押下時の処理
   */
  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    try {
      // AI画像生成API呼び出し
      const response = await postCompanyImageRegisterAi({
        companyId: acctState.company.id,
        prompt: aiPrompt,
      });
      // 画像一覧に追加
      setCompanyImagesState(prevState => [
        {
          id: response.id,
          name: response.name || aiPrompt,
          imageKey: response.imageKey,
          imageWidth: response.imageWidth,
          imageHeight: response.imageHeight,
          imageSize: response.imageSize,
          createdAt: new Date().getTime(),
        },
        ...prevState,
      ]);
      setAiPrompt('');
      // AI画像生成数の更新
      setAcctState(prev => ({
        ...prev,
        company: {
          ...prev.company,
          aiImageUsageCount: response.aiImageUsageCount,
        },
      }));
    } catch {
      noticeMessageFailure('AIによる画像生成に失敗しました。');
    } finally {
      setAiLoading(false);
    }
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート処理（hooks 専用） */
  /* ----------------------------------------------------------------------- */
  /**
   * 複数ファイルのバリデーション・並列アップロード・state更新を行います。
   */
  const _processFiles = async (files: File[]) => {
    // バリデーション
    const validationError = validateFiles(files, MAX_UPLOAD_FILES, MAX_FILE_SIZE, imageTypes);
    if (validationError !== null) {
      setUploadErrorState(validationError);
      return;
    }

    // 前回エラーをクリアし、ローディング開始
    setUploadErrorState(null);
    setProcessing(true);

    try {
      const { succeeded, failedFiles } = await processFilesUpload({
        files,
        companyId: acctState.company.id,
        postCompanyImageRegister,
      });

      if (succeeded.length > 0) {
        setCompanyImagesState(prev => [...succeeded, ...prev]);
      }
      if (failedFiles.length > 0) {
        setUploadErrorState(buildFailedErrorMessage(failedFiles));
      }
    } finally {
      setProcessing(false);
    }
  };

  return {
    /* state */
    initialized,
    processing,
    keywordState,
    sortState,
    selectedImageState,
    companyImagesState,
    dropdownOpen,
    uploadErrorState,
    isDragging,
    tab,
    aiPrompt,
    aiLoading,
    /* refs */
    inputFileRef,
    dropdownRef,
    /* setters */
    setTab,
    setAiPrompt,
    setKeywordState,
    /* event handlers */
    onAfterOpen,
    handleClose,
    handleOpenFileUploader,
    handleSelectImage,
    handleImgFileInput,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDelete,
    handleSubmit,
    handleToggleDropdown,
    handleSort,
    handleAiGenerate,
    /* computed values */
    activePlan,
    isAiImageCountLimit,
  };
}
